// Third Party Imports
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

// Artifactory Imports
import {SVGIcon} from '@homeaway/react-svg';

// Primary Imports
import {
    MESSAGE_TYPE,
    ANIMATION_TYPE,
    SETTINGS,
    inlineMessageUtil
} from './utils/inlineMessageUtil';

// styles
import './InlineMessageStateless.less';

class InlineMessageStateless extends Component {
    static propTypes = {
        /** Should the message animate; should be passed in conjunction with a new key value to reset the component. */
        animate: PropTypes.bool,
        /** Animation type to use; selected from object of options */
        animationType: PropTypes.shape({
            iconToIcon: PropTypes.bool,
            iconToHelp: PropTypes.bool,
            helpToIcon: PropTypes.bool,
            iconToNone: PropTypes.bool,
            noneToIcon: PropTypes.bool,
            anyToNone: PropTypes.bool,
            noneToAny: PropTypes.bool,
            noAnimation: PropTypes.bool,
            type: PropTypes.string
        }),
        /** The aria-live value for the alert.
         * 'polite' - indicates that the screen reader should wait until the user is idle before presenting updates to the user
         * 'assertive' may interupt user's flow
         */
        ariaLive: PropTypes.oneOf(['assertive', 'polite']),
        /** The message's id, required for a11y */
        id: PropTypes.string.isRequired,
        /** Flag to make message text use white text for better a11y contrast when used on dark backgrounds */
        inverse: PropTypes.bool,
        /** The content of the message */
        message: PropTypes.node,
        /** The previous type of message; used for applying class `.typeX-to-typeY` */
        prevType: PropTypes.oneOf([
            'danger',
            'help',
            'info',
            'none',
            'success',
            'warn'
        ]),
        /** Should the message shake. */
        shake: PropTypes.bool,
        /** The type of message */
        type: PropTypes.oneOf([
            'danger',
            'help',
            'info',
            'none',
            'success',
            'warn'
        ])
    };

    static defaultProps = {
        animate: false,
        animationType: {},
        ariaLive: 'polite',
        inverse: false,
        message: '',
        prevType: 'help',
        shake: false,
        type: 'help'
    };

    constructor(props) {
        super(props);

        // hash of timeouts if needed for cancelling
        this.timeouts = {};
    }

    componentDidMount() {
        const {animate, shake} = this.props;

        if (shake) {
            this.addShakeTimeout();
        } else if (animate) {
            this.addAnimationTimeout();
        }
    }

    componentWillUnmount() {
        const currentlyShaking = !!this.timeouts[ANIMATION_TYPE.SHAKE];
        const currentlyAnimating = !!this.timeouts[ANIMATION_TYPE.TRANSITION];

        if (currentlyShaking) {
            if (this.props.shakeTimeoutCallback) {
                this.props.shakeTimeoutCallback();
            }
            this.clearAndDeleteTimeouts(ANIMATION_TYPE.SHAKE);
        }

        if (currentlyAnimating) {
            this.clearAndDeleteTimeouts(ANIMATION_TYPE.TRANSITION);
        }
    }

    clearAndDeleteTimeouts(type) {
        clearTimeout(this.timeouts[type]);
        delete this.timeouts[type];
    }

    addAnimationTimeout = () => {
        const {noAnimation} = this.props.animationType;
        this.clearAndDeleteTimeouts(ANIMATION_TYPE.TRANSITION);
        this.timeouts[ANIMATION_TYPE.TRANSITION] = setTimeout(
            () => {
                delete this.timeouts[ANIMATION_TYPE.TRANSITION];
            },
            noAnimation ? 0 : SETTINGS.TRANSITION_DURATION
        );
    };

    addShakeTimeout = () => {
        this.clearAndDeleteTimeouts(ANIMATION_TYPE.SHAKE);
        this.timeouts[ANIMATION_TYPE.SHAKE] = setTimeout(() => {
            if (this.props.shakeTimeoutCallback) {
                this.props.shakeTimeoutCallback();
            }
            delete this.timeouts[ANIMATION_TYPE.SHAKE];
        }, SETTINGS.SHAKE_DURATION);
    };

    // eslint-disable-next-line complexity
    getClassNames(prefix) {
        const {animate, animationType, prevType, type} = this.props;

        const {
            anyToNone,
            helpToIcon,
            iconToHelp,
            iconToIcon,
            iconToNone,
            noAnimation,
            noneToAny,
            noneToIcon
        } = animationType;

        const isAnimating = animate;
        return classnames('', {
            [`${prefix}${type}`]: type,
            [`${prefix}${prevType}-to-${type}`]: isAnimating,
            [`${prefix}icon-to-icon`]: isAnimating && iconToIcon, // icon to icon
            [`${prefix}icon-to-help`]: isAnimating && iconToHelp, // icon to help
            [`${prefix}help-to-icon`]: isAnimating && helpToIcon, // help to icon
            [`${prefix}icon-to-none`]: isAnimating && iconToNone, // icon to none
            [`${prefix}none-to-icon`]: isAnimating && noneToIcon, // none to icon
            [`${prefix}any-to-none`]: isAnimating && anyToNone, // any to none
            [`${prefix}none-to-any`]: isAnimating && noneToAny, // none to any
            [`${prefix}no-animation`]: noAnimation // no animation
        });
    }

    // eslint-disable-next-line complexity
    render() {
        const {
            animate,
            ariaLive,
            id,
            inverse,
            message: nextMessage,
            prevMessage,
            prevType,
            shake,
            type: nextType
        } = this.props;

        const wrapperClassNames = classnames('InlineMessageStateless', {
            'InlineMessageStateless--shake': shake,
            'text-white': inverse
        });

        // if animating out (i.e. going to no message or help), still show the previous icon
        const iconMarkup = inlineMessageUtil.getIconMarkup({
            type: nextType,
            prevType,
            animate
        });
        const iconType = inlineMessageUtil.getIconTypeToUse({
            type: nextType,
            prevType,
            animate
        });
        const iconClassNames = classnames(
            'InlineMessageStateless__icon',
            'InlineMessageStateless--small-text',
            'small',
            this.getClassNames('InlineMessageStateless__icon--'),
            {
                'text-info': iconType === MESSAGE_TYPE.INFO && !inverse,
                'text-success': iconType === MESSAGE_TYPE.SUCCESS && !inverse,
                'text-danger': iconType === MESSAGE_TYPE.ERROR && !inverse
            }
        );

        // if animating out (i.e. going to no message), still show the previous message
        const message =
            animate && nextType === MESSAGE_TYPE.NONE
                ? prevMessage
                : nextMessage;
        const messageClassNames = classnames(
            'InlineMessageStateless__message',
            'InlineMessageStateless--small-text',
            'small',
            this.getClassNames('InlineMessageStateless__message--'),
            {
                'InlineMessageStateless__message--same-message':
                    prevMessage === nextMessage &&
                    nextType !== MESSAGE_TYPE.NONE
            }
        );
        const typeRequiresAriaLive = [
            nextType === MESSAGE_TYPE.ERROR,
            nextType === MESSAGE_TYPE.SUCCESS,
            nextType === MESSAGE_TYPE.INFO,
            nextType === MESSAGE_TYPE.WARN
        ].some((condition) => !!condition);
        const messageIsString = typeof message === 'string';
        const messageProps = {
            id,
            ...(typeRequiresAriaLive &&
                message !== '' && {role: 'alert', 'aria-live': ariaLive})
        };

        return (
            <div className={wrapperClassNames}>
                {iconMarkup && (
                    <SVGIcon className={iconClassNames} markup={iconMarkup} />
                )}
                {message && messageIsString && (
                    <span
                        {...messageProps}
                        className={messageClassNames}
                        dangerouslySetInnerHTML={{__html: message}}
                    />
                )}
                {message && !messageIsString && (
                    <div {...messageProps} className={messageClassNames}>
                        {message}
                    </div>
                )}
            </div>
        );
    }
}

export default InlineMessageStateless;
